<template>
  <v-container class="affiliate-admin py-8">
    <div class="d-flex flex-wrap justify-space-between align-center mb-6">
      <div>
        <div class="text-overline primary--text">
          ADMINISTRATION
        </div><h1 class="text-h4 mb-2">
          Affiliates &amp; referrals
        </h1><p class="mb-0">
          Manage your partners, trace invitations and review payouts.
        </p>
      </div>
      <v-btn
        outlined
        :loading="loading"
        @click="refresh"
      >
        Refresh
      </v-btn>
    </div>
    <v-alert
      v-if="error"
      type="error"
      outlined
    >
      {{ error }}
    </v-alert>
    <v-alert
      v-if="message"
      type="success"
      outlined
      dismissible
    >
      {{ message }}
    </v-alert>
    <v-progress-linear
      v-if="loading"
      indeterminate
      aria-label="Loading affiliate administration"
    ></v-progress-linear>
    <template v-if="overview">
      <v-row class="mb-5">
        <v-col
          v-for="stat in stats"
          :key="stat.label"
          cols="6"
          md="3"
        >
          <v-card
            outlined
            class="pa-5"
          >
            <div class="text-body-2 mb-2">
              {{ stat.label }}
            </div><strong class="text-h4">{{ stat.value }}</strong>
          </v-card>
        </v-col>
      </v-row>
      <v-card outlined>
        <v-tabs
          v-model="tab"
          show-arrows
          @change="changeTab"
        >
          <v-tab
            v-for="section in sections"
            :key="section.key"
          >
            {{ section.label }}
          </v-tab>
        </v-tabs>
        <v-divider></v-divider>
        <div
          v-if="sectionKey !== 'settings'"
          class="pa-4"
        >
          <div class="d-flex flex-wrap align-center">
            <v-text-field
              v-if="['affiliates', 'network'].includes(sectionKey)"
              v-model="search"
              label="Search name or email"
              outlined
              dense
              hide-details
              class="mr-3 mb-3 search-input"
              @keyup.enter="searchRows"
            ></v-text-field>
            <v-btn
              v-if="['affiliates', 'network'].includes(sectionKey)"
              outlined
              class="mb-3"
              @click="searchRows"
            >
              Search
            </v-btn>
            <v-chip
              v-if="filterAffiliate"
              close
              class="ml-3 mb-3"
              @click:close="clearFilter"
            >
              Affiliate: {{ filterAffiliate.display_name }}
            </v-chip>
          </div>
          <p
            v-if="sectionKey === 'network'"
            class="text-body-2"
          >
            Direct referrer and originating affiliate are preserved at every level. Level counts invitation edges from the original member.
          </p>
          <p
            v-if="sectionKey === 'commissions'"
            class="text-body-2"
          >
            Amounts include refund adjustments. A paid commission can create a debit against a future payout.
          </p>
          <p
            v-if="sectionKey === 'payouts'"
            class="text-body-2"
          >
            Prepared payouts reserve a batch of commissions. Record a payout as paid only after completing the transfer.
          </p>
          <v-data-table
            :headers="headers"
            :items="rows"
            :item-key="itemKey"
            :loading="loadingRows"
            :server-items-length="total"
            :options.sync="options"
            :footer-props="{ 'items-per-page-options': [50] }"
            no-data-text="No records found."
          >
            <template v-slot:item.created_at="{ item }">
              {{ date(item.created_at) }}
            </template>
            <template v-slot:item.available_at="{ item }">
              {{ date(item.available_at) }}
            </template>
            <template v-slot:item.paid_at="{ item }">
              {{ date(item.paid_at) }}
            </template>
            <template v-slot:item.amount="{ item }">
              {{ money(item.amount) }}
            </template>
            <template v-slot:item.paid_amount="{ item }">
              {{ money(item.paid_amount) }}
            </template>
            <template v-slot:item.commission_amount="{ item }">
              {{ money(item.commission_amount) }}
            </template>
            <template v-slot:item.settled_amount="{ item }">
              {{ money(item.settled_amount) }}
            </template>
            <template v-slot:item.commission_bps="{ item }">
              {{ item.commission_bps / 100 }}%
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip
                small
                outlined
                :color="statusColor(item.status)"
              >
                {{ item.status }}
              </v-chip>
            </template>
            <template v-slot:item.parent_name="{ item }">
              {{ item.parent_name || '—' }}
            </template>
            <template v-slot:item.affiliate_name="{ item }">
              {{ item.affiliate_name || '—' }}
            </template>
            <template v-slot:item.payment_kind="{ item }">
              {{ item.payment_kind === 'purchase' ? 'One-time purchase' : 'Subscription' }}
            </template>
            <template v-slot:item.disputed="{ item }">
              {{ item.disputed ? 'Under review' : item.refunded_amount ? 'Refund adjusted' : 'Clear' }}
            </template>
            <template v-slot:item.details="{ item }">
              <v-btn
                small
                text
                color="primary"
                @click="showAudit(item)"
              >
                View details
              </v-btn>
            </template>
            <template v-slot:item.actions="{ item }">
              <div
                v-if="sectionKey === 'affiliates'"
                class="admin-row-actions"
              >
                <v-btn
                  small
                  text
                  color="primary"
                  @click="editAffiliate(item)"
                >
                  Review
                </v-btn>
                <v-btn
                  small
                  text
                  @click="showNetwork(item)"
                >
                  Network
                </v-btn>
                <v-btn
                  small
                  text
                  :disabled="item.status !== 'active' || busy"
                  color="primary"
                  @click="prepareDialog(item)"
                >
                  Prepare payout
                </v-btn>
              </div>
              <div
                v-else-if="sectionKey === 'payouts' && item.status === 'prepared'"
                class="admin-row-actions"
              >
                <v-btn
                  small
                  text
                  color="primary"
                  @click="payoutDialog(item, 'paid')"
                >
                  Record payment
                </v-btn>
                <v-btn
                  small
                  text
                  color="error"
                  @click="payoutDialog(item, 'cancelled')"
                >
                  Cancel
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </div>
        <v-form
          v-else
          class="pa-6"
          @submit.prevent="saveSettings"
        >
          <h2 class="text-h6 mb-3">
            Program rules
          </h2>
          <p>Commission and reward terms are saved when each new customer joins. Changes apply to future attributions. The payout minimum applies to new payout batches. Invitations and network depth are unlimited.</p>
          <v-row>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="commissionPercent"
                type="number"
                min="0"
                max="100"
                step="0.01"
                label="Affiliate commission (%)"
                outlined
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="settings.commission_months"
                type="number"
                min="1"
                max="120"
                label="Commission duration (months)"
                outlined
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="settings.hold_days"
                type="number"
                min="1"
                max="180"
                label="Validation period (days)"
                outlined
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="settings.friend_credits"
                type="number"
                min="0"
                max="10000"
                label="AI credits per person"
                outlined
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="settings.credit_expiry_days"
                type="number"
                min="1"
                max="3650"
                label="Credit validity after release (days)"
                outlined
              ></v-text-field>
            </v-col>
            <v-col
              cols="12"
              sm="6"
            >
              <v-text-field
                v-model.number="payoutMinimum"
                type="number"
                min="0.01"
                max="10000"
                step="0.01"
                label="Minimum payout (USD)"
                outlined
              ></v-text-field>
            </v-col>
          </v-row>
          <v-btn
            color="primary"
            type="submit"
            :loading="busy"
          >
            Save program rules
          </v-btn>
        </v-form>
      </v-card>
    </template>

    <v-dialog
      v-model="dialog"
      max-width="640"
      :persistent="busy"
    >
      <v-card>
        <v-card-title>{{ dialogTitle }}</v-card-title>
        <v-card-text>
          <template v-if="dialogType === 'affiliate'">
            <p>Application eligibility follows <router-link to="/admin/settings">App Settings</router-link>. Changes to this setting or an applicant's plan do not affect existing applications, affiliate status or commissions.</p>
            <h3 class="text-subtitle-1">
              {{ selected.display_name }}
            </h3><p>{{ selected.email }}</p><p class="preserve-lines">
              {{ selected.application }}
            </p>
            <v-select
              v-model="newStatus"
              :items="['active', 'suspended', 'rejected']"
              label="Status"
              outlined
            ></v-select>
            <v-textarea
              v-model="reason"
              label="Reason (shown to the affiliate if rejected or suspended)"
              outlined
              rows="2"
              :maxlength="500"
            ></v-textarea>
            <p>Suspension disables new direct affiliate links and payouts. Existing customer attribution and commission history remain traceable.</p>
          </template>
          <template v-else-if="dialogType === 'prepare'">
            <p>Prepare a payout for <strong>{{ selected.display_name }}</strong>.</p>
            <p>Available balance: <strong>{{ money(payoutBalance) }}</strong>. Minimum: {{ money(overview.rules.payout_minimum) }}.</p>
            <p>This reserves up to 100 eligible purchases after checking their payment status and applying outstanding debits. The transfer is completed separately.</p>
          </template>
          <template v-else-if="dialogType === 'paid' || dialogType === 'cancelled'">
            <p>{{ selected.display_name }} · <strong>{{ money(selected.amount) }}</strong></p>
            <p v-if="dialogType === 'paid'">
              Record the payment you have completed for this batch. Keep the bank or payment-provider reference for reconciliation.
            </p>
            <p v-else>
              Cancel this batch and release its commissions for a future payout.
            </p>
            <v-text-field
              v-model="reason"
              :label="dialogType === 'paid' ? 'Payment reference / receipt URL' : 'Cancellation reason'"
              outlined
              :maxlength="255"
            ></v-text-field>
          </template>
          <template v-else-if="dialogType === 'audit'">
            <p>{{ selected.action }} · {{ date(selected.created_at) }}</p><p>Actor: {{ selected.actor_sub }}</p><pre class="audit-details">{{ auditDetails }}</pre>
          </template>
          <v-alert
            v-if="dialogError"
            type="error"
            outlined
          >
            {{ dialogError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer><v-btn
            text
            :disabled="busy"
            @click="dialog = false"
          >
            Close
          </v-btn><v-btn
            v-if="dialogType !== 'audit'"
            color="primary"
            :loading="busy"
            :disabled="dialogType !== 'prepare' && reason.trim().length < 3"
            @click="confirmDialog"
          >
            {{ dialogType === 'prepare' ? 'Prepare batch' : dialogType === 'paid' ? 'Record as paid' : 'Save' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import referrals from '@/services/referrals'
import { newRequestKey } from '@/services/billing'

const columns = {
  affiliates: [['Name', 'display_name'], ['Email', 'email'], ['Status', 'status'], ['Applied', 'created_at'], ['Actions', 'actions']],
  network: [['Member', 'display_name'], ['Email', 'email'], ['Direct referrer', 'parent_name'], ['Originating affiliate', 'affiliate_name'], ['Source', 'source_kind'], ['Level', 'depth'], ['Joined', 'created_at']],
  commissions: [['Customer', 'customer_name'], ['Affiliate', 'affiliate_name'], ['Purchase', 'invoice_id'], ['Type', 'payment_kind'], ['Payment', 'paid_amount'], ['Rate', 'commission_bps'], ['Commission', 'commission_amount'], ['Paid out', 'settled_amount'], ['Release', 'available_at'], ['Review', 'disputed']],
  rewards: [['Member', 'display_name'], ['Credits', 'credits'], ['Status', 'status'], ['Release', 'available_at'], ['Invoice', 'invoice_id']],
  payouts: [['Affiliate', 'display_name'], ['Amount', 'amount'], ['Status', 'status'], ['Created', 'created_at'], ['Reference', 'payment_reference'], ['Actions', 'actions']],
  audit: [['Date', 'created_at'], ['Action', 'action'], ['Actor', 'actor_sub'], ['Target', 'target_id'], ['Details', 'details']],
}

export default {
  name: 'AdminAffiliates',
  data: () => ({
    overview: null,
    loading: false,
    loadingRows: false,
    busy: false,
    error: '',
    message: '',
    tab: 0,
    rows: [],
    total: 0,
    options: { page: 1, itemsPerPage: 50 },
    search: '',
    filterAffiliate: null,
    requestNumber: 0,
    sections: [{ key: 'affiliates', label: 'Affiliates' }, { key: 'network', label: 'Referral network' }, { key: 'commissions', label: 'Commissions' }, { key: 'rewards', label: 'Referral rewards' }, { key: 'payouts', label: 'Payouts' }, { key: 'settings', label: 'Settings' }, { key: 'audit', label: 'Audit log' }],
    settings: {},
    commissionPercent: 25,
    payoutMinimum: 50,
    dialog: false,
    dialogType: '',
    dialogError: '',
    selected: {},
    newStatus: 'active',
    reason: '',
    requestKey: '',
    payoutBalance: 0,
  }),
  computed: {
    sectionKey() { return this.sections[this.tab].key },
    headers() { return (columns[this.sectionKey] || []).map(([text, value]) => ({ text, value, sortable: false })) },
    itemKey() {
      if (['affiliates', 'network'].includes(this.sectionKey)) return 'account_id'

      return this.sectionKey === 'commissions' ? 'invoice_id' : 'id'
    },
    stats() {
      const o = this.overview

      return [{ label: 'Active affiliates', value: Number(o.counts.active || 0) }, { label: 'Awaiting approval', value: Number(o.counts.applications || 0) }, { label: 'Attributed members', value: Number(o.network.attributed || 0) }, { label: 'Commissions paid', value: this.money(o.totals.paid) }, { label: 'Pending validation', value: this.money(o.totals.pending) }, { label: 'Available balance', value: this.money(o.totals.available) }, { label: 'Reserved for payouts', value: this.money(o.totals.reserved) }, { label: 'Under review', value: this.money(o.totals.blocked) }]
    },
    dialogTitle() {
      return {
        affiliate: 'Review affiliate', prepare: 'Prepare payout', paid: 'Record completed payment', cancelled: 'Cancel payout', audit: 'Audit details',
      }[this.dialogType] || ''
    },
    auditDetails() { try { return JSON.stringify(typeof this.selected.details === 'string' ? JSON.parse(this.selected.details) : this.selected.details, null, 2) } catch (_) { return 'Details unavailable.' } },
  },
  watch: { 'options.page': function pageChanged() { if (this.overview) this.loadRows() } },
  mounted() { this.refresh() },
  methods: {
    money(value) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format((value || 0) / 100) },
    date(value) { return value ? new Date(value).toLocaleString() : '—' },
    statusColor(status) {
      return {
        active: 'success', paid: 'success', granted: 'success', pending: 'warning', prepared: 'info', suspended: 'error', rejected: 'error', blocked: 'error',
      }[status] || 'grey'
    },
    errorText(error) { return (error.response && error.response.data.message) || 'The operation could not be completed. Please try again.' },
    async refresh() {
      this.loading = true; this.error = ''
      try {
        this.overview = await referrals.admin('overview')
        this.settings = { ...this.overview.rules }; this.commissionPercent = this.settings.commission_bps / 100; this.payoutMinimum = this.settings.payout_minimum / 100
        await this.loadRows()
      } catch (error) { this.error = this.errorText(error) } finally { this.loading = false }
    },
    async loadRows() {
      if (this.sectionKey === 'settings') return
      this.requestNumber += 1
      const request = this.requestNumber
      this.loadingRows = true; this.error = ''
      try {
        const result = await referrals.admin(this.sectionKey, { page: this.options.page, search: this.search, affiliateId: this.filterAffiliate ? this.filterAffiliate.account_id : undefined })
        if (request === this.requestNumber) { this.rows = result.rows; this.total = result.total }
      } catch (error) { if (request === this.requestNumber) this.error = this.errorText(error) } finally { if (request === this.requestNumber) this.loadingRows = false }
    },
    changeTab() { this.rows = []; this.search = ''; this.options.page = 1; this.loadRows() },
    searchRows() { this.options.page = 1; this.loadRows() },
    clearFilter() { this.filterAffiliate = null; this.searchRows() },
    showNetwork(item) { this.filterAffiliate = item; this.tab = 1; this.changeTab() },
    openDialog(type, item) { this.dialogType = type; this.selected = item; this.reason = ''; this.dialogError = ''; this.dialog = true },
    editAffiliate(item) { this.newStatus = item.status === 'pending' ? 'active' : item.status; this.openDialog('affiliate', item) },
    showAudit(item) { this.openDialog('audit', item) },
    payoutDialog(item, status) { this.openDialog(status, item) },
    async prepareDialog(item) {
      this.busy = true
      try { const detail = await referrals.admin('overview', { affiliateId: item.account_id }); this.payoutBalance = detail.totals.available; this.requestKey = newRequestKey(); this.openDialog('prepare', item) } catch (error) { this.error = this.errorText(error) } finally { this.busy = false }
    },
    async confirmDialog() {
      this.busy = true; this.dialogError = ''
      try {
        if (this.dialogType === 'affiliate') await referrals.updateAffiliate(this.selected.account_id, this.newStatus, this.reason)
        else if (this.dialogType === 'prepare') {
          let payout
          do {
            // Each request advances payment reconciliation in a bounded batch.
            // eslint-disable-next-line no-await-in-loop
            payout = await referrals.preparePayout(this.selected.account_id, this.requestKey)
          } while (payout.reconciling)
          this.message = `Payout batch prepared: ${this.money(payout.amount)}.`
        } else await referrals.finishPayout(this.selected.id, this.dialogType, this.reason)
        this.dialog = false; await this.refresh()
      } catch (error) { this.dialogError = this.errorText(error) } finally { this.busy = false }
    },
    async saveSettings() {
      this.busy = true; this.error = ''
      const body = {
        commission_bps: Math.round(this.commissionPercent * 100),
        commission_months: this.settings.commission_months,
        hold_days: this.settings.hold_days,
        friend_credits: this.settings.friend_credits,
        credit_expiry_days: this.settings.credit_expiry_days,
        payout_minimum: Math.round(this.payoutMinimum * 100),
      }
      try { await referrals.updateSettings(body); this.message = 'Program rules saved. Existing attributions retain their terms.'; await this.refresh() } catch (error) { this.error = this.errorText(error) } finally { this.busy = false }
    },
  },
}
</script>

<style scoped>
.affiliate-admin { max-width: 1500px; }
.search-input { max-width: 340px; }
.admin-row-actions { display: flex; flex-wrap: wrap; min-width: 210px; }
.preserve-lines { white-space: pre-wrap; }
.audit-details { white-space: pre-wrap; overflow-wrap: anywhere; font-size: 12px; }
</style>
